//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ContractNameEnum } from './ContractCTO'
import { ChainId, yupEthAddrRequired, yupTxHashOptional } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { EventNameForTemplate, EventTypeForTemplate } from './sharedTypes'

export enum TransactionStatus {
  Presigned = 'Presigned',
  Signed = 'Signed',
  Mined = 'Mined',
}

export const TransactionCTOSchema = yup.object({
  id: yup
    .string()
    .label('PoolEventId ')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of event ', stringType: StringTypeEnum.ID })),
  address: yupEthAddrRequired.label('Address').meta(
    toFieldMetaData({
      propName: 'address',
      description: 'address of the contract ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  sender: yupEthAddrRequired.label('sender').meta(
    toFieldMetaData({
      propName: 'sender',
      description: 'sender of the transaction ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  status: yup
    .mixed<TransactionStatus>()
    .oneOf(Object.values(TransactionStatus) as unknown as TransactionStatus[])
    .defined()
    .label('Status')
    .meta(toFieldMetaData({ propName: 'status', description: 'Status of the transaction' })),
  chainId: yup
    .mixed<ChainId>()
    .oneOf(Object.values(ChainId) as unknown as ChainId[])
    .defined()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', description: 'ChainId of the transaction' })),
  contractName: yup
    .mixed<ContractNameEnum>()
    .oneOf(Object.values(ContractNameEnum) as unknown as ContractNameEnum[])
    .defined()
    .label('Contract Name')
    .meta(toFieldMetaData({ propName: 'contractName', description: 'ContractName of the transaction' })),
  methodName: yup
    .string()
    .label('Method Name')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'methodName', description: 'Name of the contract method' })),
  transactionHash: yupTxHashOptional.label('Transaction Hash').meta(
    toFieldMetaData({
      propName: 'transactionHash',
      stringType: StringTypeEnum.TX_HASH,
    })
  ),
  eventType: yup
    .mixed<EventTypeForTemplate>()
    .oneOf(Object.values(EventTypeForTemplate))
    .defined()
    .label('Event Label')
    .default(EventTypeForTemplate.Undefined)
    .meta(toFieldMetaData({ propName: 'eventType', description: 'Name of Event' })),
  eventName: yup
    .mixed<EventNameForTemplate>()
    .oneOf([EventNameForTemplate.Undefined, EventNameForTemplate.AccountCredit])
    .defined()
    .label('Event Name')
    .default(EventNameForTemplate.Undefined)
    .meta(
      toFieldMetaData({
        propName: 'eventName',
        description: 'Name of Event',
      })
    ),
  args: yup
    .array()
    .of(yup.mixed<string | number | bigint | boolean>().required())
    .required()
    .label('Arguments')
    .meta(toFieldMetaData({ propName: 'args' })),
  presignedTimestamp: yup
    .number()
    .required()
    .label('Creation Timestamp')
    .meta(toFieldMetaData({ propName: 'creationTimestamp', stringType: StringTypeEnum.ETH_DATE })),
  signedTimestamp: yup
    .number()
    .required()
    .label('Execution Timestamp')
    .meta(toFieldMetaData({ propName: 'executedTimestamp', stringType: StringTypeEnum.ETH_DATE })),
  minedTimestamp: yup
    .number()
    .required()
    .label('Execution Timestamp')
    .meta(toFieldMetaData({ propName: 'executedTimestamp', stringType: StringTypeEnum.ETH_DATE })),
})

export interface TransactionCTO {
  id: string
  address: EthAddr
  sender: EthAddr
  status: TransactionStatus
  chainId: ChainId
  contractName: ContractNameEnum
  methodName: string
  eventType: EventTypeForTemplate
  eventName: EventNameForTemplate
  transactionHash?: string
  args: Array<string | number | bigint | boolean>
  presignedTimestamp: number
  signedTimestamp: number
  minedTimestamp: number
}

type TransactionCTOInfer = InferType<typeof TransactionCTOSchema>

export const _testALytEvent: TransactionCTO = {} as unknown as TransactionCTOInfer
export const _testBLytEvent: TransactionCTOInfer = {} as unknown as TransactionCTO
